import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Layers, Plus, Loader2, Archive, ArchiveRestore, Trash2, Check, Pencil, X } from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { invalidateImportCategories } from '../DealFilterPanel';

export const STAGE_SHORT = { ai_reviewed: 'OLTA TAM', succession_possible: 'Succession Possible' };

/** Inline "name + stage" form — shared by the card and the upload dropdown ("+ Neue Kategorie"). */
export function NewCategoryForm({ onCreated, onCancel, compact = false }) {
  const [name, setName] = useState('');
  const [stage, setStage] = useState('succession_possible');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const r = await api.createImportCategory({ name: name.trim(), stage });
      invalidateImportCategories();
      toast.success(`Kategorie „${r.data.name}“ angelegt → ${STAGE_SHORT[r.data.stage]}`);
      setName('');
      onCreated && onCreated(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Anlegen fehlgeschlagen'); }
    setBusy(false);
  };
  return (
    <div className={`flex items-center gap-2 flex-wrap ${compact ? '' : 'border border-dashed border-border rounded-sm p-2.5'}`} data-testid="new-category-form">
      <Input value={name} onChange={e => setName(e.target.value)} placeholder="Name, z. B. Orbis Süd 2026" data-testid="new-category-name-input"
        onKeyDown={e => e.key === 'Enter' && submit()} className="bg-secondary border-border rounded-sm h-8 text-xs w-52" />
      <Select value={stage} onValueChange={setStage}>
        <SelectTrigger data-testid="new-category-stage-select" className="w-48 bg-secondary border-border rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-card border-border rounded-sm">
          <SelectItem value="ai_reviewed" className="text-xs">→ OLTA TAM (AI Reviewed)</SelectItem>
          <SelectItem value="succession_possible" className="text-xs">→ Succession Possible</SelectItem>
        </SelectContent>
      </Select>
      <Button size="sm" onClick={submit} disabled={busy || !name.trim()} data-testid="new-category-save-btn"
        className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm h-8 text-xs px-3">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Plus className="w-3 h-3 mr-1" /> Anlegen</>}
      </Button>
      {onCancel && <Button size="sm" variant="ghost" onClick={onCancel} className="h-8 text-xs text-muted-foreground" data-testid="new-category-cancel-btn">Abbrechen</Button>}
    </div>
  );
}

function CategoryRow({ cat, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(cat.name);
  const [busy, setBusy] = useState(false);
  const update = async (patch, okMsg) => {
    setBusy(true);
    try {
      const r = await api.updateImportCategory(cat.id, patch);
      invalidateImportCategories();
      if (r.data.still_in_old_stage != null) {
        toast.info(`Verknüpfung geändert → ${STAGE_SHORT[r.data.stage]}. Gilt für künftige Importe — ${r.data.still_in_old_stage} Deal${r.data.still_in_old_stage === 1 ? '' : 's'} liegen weiterhin in „${r.data.old_stage_label}“.`, { duration: 8000 });
      } else if (okMsg) toast.success(okMsg);
      setEditing(false);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm(`Kategorie „${cat.name}“ endgültig löschen?`)) return;
    setBusy(true);
    try { await api.deleteImportCategory(cat.id); invalidateImportCategories(); toast.success('Kategorie gelöscht'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen'); }
    setBusy(false);
  };
  return (
    <div className={`flex items-center gap-2 flex-wrap border border-border rounded-sm px-3 py-2 ${cat.archived ? 'opacity-60' : ''}`} data-testid={`import-category-row-${cat.id}`}>
      {editing ? (
        <>
          <Input value={name} onChange={e => setName(e.target.value)} className="bg-secondary border-border rounded-sm h-7 text-xs w-48" data-testid={`category-name-input-${cat.id}`}
            onKeyDown={e => { if (e.key === 'Enter') update({ name }, 'Umbenannt'); if (e.key === 'Escape') setEditing(false); }} autoFocus />
          <button onClick={() => update({ name }, 'Umbenannt')} disabled={busy} className="text-emerald-400 hover:text-emerald-300" data-testid={`category-name-save-${cat.id}`}><Check className="w-4 h-4" /></button>
          <button onClick={() => { setEditing(false); setName(cat.name); }} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </>
      ) : (
        <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 text-sm text-foreground hover:text-blue-300 group" data-testid={`category-name-${cat.id}`} title="Umbenennen">
          {cat.name}{cat.archived && <span className="text-[9px] font-mono text-muted-foreground uppercase">archiviert</span>}
          <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-60" />
        </button>
      )}
      <span className="text-[10px] text-muted-foreground">→</span>
      <Select value={cat.stage} onValueChange={v => v !== cat.stage && update({ stage: v })} disabled={busy}>
        <SelectTrigger data-testid={`category-stage-select-${cat.id}`} className="w-44 bg-secondary border-border rounded-sm h-7 text-[11px]"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-card border-border rounded-sm">
          <SelectItem value="ai_reviewed" className="text-xs">OLTA TAM (AI Reviewed)</SelectItem>
          <SelectItem value="succession_possible" className="text-xs">Succession Possible</SelectItem>
        </SelectContent>
      </Select>
      <span className="text-[11px] font-mono text-muted-foreground ml-auto" data-testid={`category-count-${cat.id}`}>{(cat.deal_count || 0).toLocaleString('de-DE')} Deals</span>
      <button onClick={() => update({ archived: !cat.archived }, cat.archived ? 'Kategorie reaktiviert' : 'Kategorie archiviert — nicht mehr im Upload, weiter filterbar')} disabled={busy}
        className="text-muted-foreground hover:text-foreground p-1" title={cat.archived ? 'Reaktivieren' : 'Archivieren'} data-testid={`category-archive-${cat.id}`}>
        {cat.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
      </button>
      {!cat.deal_count && (
        <button onClick={remove} disabled={busy} className="text-red-400/70 hover:text-red-400 p-1" title="Löschen (nur leere Kategorien)" data-testid={`category-delete-${cat.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

export function ImportCategoriesCard({ onChanged, refreshKey }) {
  const [cats, setCats] = useState([]);
  const [uncategorized, setUncategorized] = useState(0);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    api.listImportCategories().then(r => { setCats(r.data.categories || []); setUncategorized(r.data.uncategorized || 0); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const changed = () => { load(); onChanged && onChanged(); };
  return (
    <div className="bg-card border border-border rounded-sm p-6 space-y-4" data-testid="import-categories-card">
      <div>
        <h2 className="font-heading text-lg text-foreground flex items-center gap-2"><Layers className="w-5 h-5 text-blue-400" /> Import-Kategorien</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Eigene Kategorien für den Batch-Upload (z. B. eine Orbis-Liste, Region oder Branche). Jede Kategorie ist mit einer Start-Stufe verknüpft —
          OLTA TAM oder Succession Possible — dort landen die importierten Deals im Funnel. Nutzer filtern im Deal-Explorer nach Kategorie.
          Eine geänderte Verknüpfung gilt nur für künftige Importe.
        </p>
      </div>
      {loading ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : (
        <div className="space-y-2">
          {cats.map(c => <CategoryRow key={c.id} cat={c} onChanged={changed} />)}
          {!cats.length && <p className="text-xs text-muted-foreground italic" data-testid="import-categories-empty">Noch keine Kategorien — unten anlegen.</p>}
          <p className="text-[10px] text-muted-foreground" data-testid="import-categories-uncategorized">{uncategorized.toLocaleString('de-DE')} Deals ohne Kategorie (Bestand vor Einführung)</p>
        </div>
      )}
      <NewCategoryForm onCreated={changed} />
    </div>
  );
}
